import React from 'react';
import { Box, Typography } from '@mui/material';
import { AudioButton } from '../../common/AudioButton';
import { HintButton } from '../../common/HintButton';
import { QUIZ_MANUAL_QUESTION_NO_HINT, SHORT_ANSWER_LENGTH } from '../../common/constants';
import {
  areOptionsShort,
  getOptionEntries,
  getOriginLabel,
  isTrueFalseQuestion,
  orderTrueFirst,
} from '../helpers';
import { AnswerBox } from './AnswerBox';
import { OptionBox } from './OptionBox';

/**
 * Builds the sentence the audio button reads out for a question.
 * @param {Number} number The question's position in the quiz, starting at 1
 * @param {Object} question The quiz question
 * @param {Array} optionEntries The question's ordered [key, text] options
 * @returns {String} The text to speak
 */
const getSpokenText = (number, question, optionEntries) =>
  [
    `Question ${number}. ${question.question}`,
    ...optionEntries.map(([key, text]) => `Option ${key}. ${text}`),
  ].join(' ');

/**
 * What one half of a graded True/False pair is called: the learner's own answer
 * is announced as such, right or wrong, and the answer they missed is simply the
 * answer.
 * @param {Object} params
 * @param {Boolean} params.isAnswer Whether this option is the correct one
 * @param {Boolean} params.isSelected Whether the learner chose this option
 * @returns {String} The box's heading
 */
const getTrueFalseHeading = ({ isAnswer, isSelected }) => {
  if (isSelected) return isAnswer ? 'You chose the correct answer!' : 'Your Answer:';
  return 'Correct Answer:';
};

/**
 * A single quiz question.
 *
 * Before submission the options are selectable. Afterwards the card switches to
 * the graded layout: the learner's answer, then the correct one when those
 * differ, then the remaining options and the explanation.
 *
 * Props:
 *   id       (string)   - ID prefix for generated element IDs.
 *   index    (number)   - Zero-based position in the quiz.
 *   result   (object)   - One entry from gradeQuiz: { question, selectedKey, isCorrect }.
 *   isGraded (bool)     - Whether the quiz has been submitted.
 *   onSelect (function) - Called with (questionId, optionKey) while taking the quiz.
 *   cardRef  (function) - Ref callback, used to scroll this card into view.
 */
export const QuestionCard = ({ id, index, result, isGraded, onSelect, cardRef }) => {
  const { question, selectedKey, isCorrect } = result;
  const number = index + 1;
  const cardId = `${id}__question_${number}`;
  const isTrueFalse = isTrueFalseQuestion(getOptionEntries(question));
  // True/False reads True then False wherever it appears, however the two were
  // keyed. Every other question keeps its lettered order.
  const optionEntries = isTrueFalse
    ? orderTrueFirst(getOptionEntries(question))
    : getOptionEntries(question);

  // The graded layout already shows these two above the "Other Answers" list.
  const highlightedKeys = isGraded ? [selectedKey, question.answer].filter(Boolean) : [];
  const otherOptions = optionEntries.filter(([key]) => !highlightedKeys.includes(key));

  // Short answers pair up two per row; long ones get the full width.
  const isCompact = areOptionsShort(optionEntries, SHORT_ANSWER_LENGTH);
  const listClass = `optionList ${isCompact ? 'compact' : ''} ${isTrueFalse ? 'trueFalse' : ''}`;

  // A question the learner wrote or rewrote themselves says so here, as it does
  // in the editor — it is not one of the generated ones, and reads differently
  // for it.
  const originLabel = getOriginLabel(question);

  return (
    <Box className="questionCard" id={cardId} ref={cardRef} component="section">
      <Box className="questionCardHeader">
        <Box className="questionCardHeading">
          <Typography className="questionNumber">Question {number}</Typography>
          {originLabel && (
            <Typography className="questionOrigin" id={`${cardId}__text_origin`}>
              {originLabel}
            </Typography>
          )}
        </Box>

        <Box className="questionCardActions">
          <HintButton
            id={cardId}
            hint={question.hint}
            label={`Show hint for question ${number}`}
            /*
             * A question the learner added themselves was never generated with a
             * hint, which is worth saying — otherwise the disabled lightbulb
             * reads as something missing.
             */
            emptyLabel={
              question.isManuallyAdded
                ? QUIZ_MANUAL_QUESTION_NO_HINT
                : `No hint available for question ${number}`
            }
          />
          <AudioButton
            id={`${cardId}__iconButton_audio`}
            text={getSpokenText(number, question, optionEntries)}
            label={`Read question ${number} aloud`}
          />
        </Box>
      </Box>

      <Typography className="questionText" id={`${cardId}__text`}>
        {question.question}
      </Typography>

      {isGraded && isTrueFalse ? (
        /*
         * True/False is read as a pair rather than a sequence: True on the left
         * and False on the right, whichever of them was chosen. The one that is
         * neither the answer nor the learner's choice is left plain.
         */
        <Box className="trueFalseResult" id={`${cardId}__trueFalseResult`}>
          {optionEntries.map(([key, text]) => {
            const isAnswer = key === question.answer;
            const isSelected = key === selectedKey;

            if (!isAnswer && !isSelected) {
              return (
                <OptionBox key={key} id={`${cardId}__option_${key}`} optionText={text} hideKey />
              );
            }

            return (
              <AnswerBox
                key={key}
                id={`${cardId}__${isAnswer ? 'correctAnswer' : 'selectedAnswer'}`}
                variant={isAnswer ? 'correct' : 'incorrect'}
                heading={getTrueFalseHeading({ isAnswer, isSelected })}
                optionText={text}
                hideKey
                // Only a wrong answer needs explaining; a correct one speaks for itself.
                explanation={isAnswer ? undefined : question.explanation}
              />
            );
          })}
        </Box>
      ) : isGraded ? (
        <>
          {selectedKey ? (
            <AnswerBox
              id={`${cardId}__selectedAnswer`}
              variant={isCorrect ? 'correct' : 'incorrect'}
              heading={
                isCorrect ? 'You chose the correct answer!' : 'You chose an incorrect answer:'
              }
              optionKey={selectedKey}
              optionText={question.options?.[selectedKey]}
              hideKey={isTrueFalse}
              // Only a wrong answer needs explaining; a correct one speaks for itself.
              explanation={isCorrect ? undefined : question.explanation}
            />
          ) : (
            /*
             * Submitting requires every question answered, so this is only a
             * guard against a submission that arrived some other way.
             */
            <Typography className="unansweredNotice" id={`${cardId}__unanswered`}>
              You did not answer this question.
            </Typography>
          )}

          {!isCorrect && (
            <AnswerBox
              id={`${cardId}__correctAnswer`}
              variant="correct"
              heading="The correct answer is:"
              optionKey={question.answer}
              optionText={question.options?.[question.answer]}
              hideKey={isTrueFalse}
            />
          )}

          {/*
            True/False has only the two answers, both already shown above, so
            listing the leftover one adds nothing.
          */}
          {otherOptions.length > 0 && !isTrueFalse && (
            <>
              <Typography className="otherAnswersLabel">Other Answers:</Typography>
              <Box className={listClass}>
                {otherOptions.map(([key, text]) => (
                  <OptionBox
                    key={key}
                    id={`${cardId}__option_${key}`}
                    optionKey={key}
                    optionText={text}
                  />
                ))}
              </Box>
            </>
          )}
        </>
      ) : (
        <Box className={listClass} role="radiogroup" aria-labelledby={`${cardId}__text`}>
          {optionEntries.map(([key, text]) => (
            <OptionBox
              key={key}
              id={`${cardId}__option_${key}`}
              optionKey={key}
              optionText={text}
              isSelected={selectedKey === key}
              hideKey={isTrueFalse}
              onSelect={() => onSelect(question.id, key)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
